import { container } from '@/app/container';
import { SALES_ROLES } from '@/shared/guards/roles';
import type {
  PendingAlertDetailRow,
  PendingAlertStatus,
  PendingSource,
} from '@/modules/pending-alerts/domain/entities/pending-alert.entity';
import {
  SALES_OPERATIONAL_ALERT_ENTITY_TYPE,
  type DuplicatePaymentDetails,
  type PendingInvoiceBeyondLimitCase,
  type SalesOperationalAlert,
} from '../domain/entities/sales.entity';
import { SalesRepositoryToken } from '../di/tokens';
import { FOCUSED_INVOICE_PARAM } from './shared/invoices';
import { eventLabel } from './shared/event-labels';

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Aberto',
  RESOLVED: 'Resolvido',
  DISMISSED: 'Descartado',
};

/**
 * Rótulos do vocabulário que o Sales grava hoje (`SalesAlertType` do
 * monolith), em pt-BR e dizendo **a coisa**, não o nome da constante.
 *
 * `type` continua `string` aberta no contrato HTTP — o back pode ganhar um
 * tipo novo antes desta tela aprender o nome dele —, então o que não estiver
 * aqui cai no fallback em vez de sumir da fila.
 */
const ALERT_TYPE_LABEL: Record<string, string> = {
  provider_account_not_recognized: 'Conta de gateway não reconhecida',
  pending_invoices_beyond_limit: 'Faturas paradas há meses',
  duplicate_payment: 'Pagamento duplicado',
};

/**
 * Tipos de grão `'entity'` no back (um aberto por entidade): os únicos cujo
 * fechamento aceita `closeForEntity` — nos outros o back recusa.
 */
const CLOSABLE_FOR_ENTITY = new Set(['duplicate_payment']);

/** `SalesEntityType` do monolith, no recorte que um alerta referencia. */
const ALERT_ENTITY_TYPE_LABEL: Record<string, string> = {
  ProviderAccount: 'Conta de gateway',
  Invoice: 'Fatura',
};

/**
 * Rótulo legível a partir de uma chave `snake_case` ainda sem tradução —
 * mesmo fallback do `totvsPendingSource`.
 */
function humanizeSnakeCase(value: string): string {
  const words = value.split('_').filter(Boolean);
  if (words.length === 0) return value;
  const [first, ...rest] = words as [string, ...string[]];
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ');
}

/**
 * Para onde vai quem clica no id da entidade afetada.
 *
 * Só `Invoice` tem destino: fatura não tem tela própria, ela é uma linha da
 * página da BA que a emitiu, aberta em foco por `?invoice=`.
 *
 * A BA sai de onde cada escritor a grava: o pagamento duplicado, na raiz de
 * `details`; as faturas paradas, no **caso correspondente** em `details.cases`
 * (`{ limitDays, cases, truncated }`).
 *
 * **`ProviderAccount` não tem destino, e isso é o próprio alerta:** o
 * `entityId` ali é o nome do provider (`guru`), e a conta não existe — é essa
 * a pendência. Id sem rota fica texto puro, nunca link inventado.
 */
function alertEntityHref(alert: SalesOperationalAlert): string | undefined {
  if (alert.entityType !== 'Invoice') return undefined;
  const billingAgreementId =
    alert.type === 'duplicate_payment'
      ? duplicatePaymentDetails(alert).billingAgreementId
      : listedCases(alert).find((one) => one.invoiceId === alert.entityId)
          ?.billingAgreementId;
  if (!billingAgreementId) return undefined;
  return `/billing-agreements/${billingAgreementId}?${FOCUSED_INVOICE_PARAM}=${alert.entityId}`;
}

function duplicatePaymentDetails(
  alert: SalesOperationalAlert,
): DuplicatePaymentDetails {
  return alert.details as DuplicatePaymentDetails;
}

/**
 * Fatura, contrato, BA e as transações — a duplicada (uma por reincidência,
 * que o back mescla no mesmo alerta) e as pagas antes dela.
 */
function duplicatePaymentRows(
  alert: SalesOperationalAlert,
): PendingAlertDetailRow[] {
  const details = duplicatePaymentDetails(alert);
  const rows: PendingAlertDetailRow[] = [];
  if (details.contractId)
    rows.push({
      label: 'Contrato',
      value: details.contractId,
      href: `/contracts/${details.contractId}`,
    });
  if (details.billingAgreementId)
    rows.push({
      label: 'Billing agreement',
      value: details.billingAgreementId,
      href: `/billing-agreements/${details.billingAgreementId}`,
    });
  const transactions = (label: string, ids: unknown) => {
    if (!Array.isArray(ids)) return;
    for (const id of ids as string[])
      rows.push({ label, value: id, href: `/sales/${id}` });
  };
  transactions('Transação duplicada', details.duplicateTransactionIds);
  transactions('Paga antes', details.previousPaidTransactionIds);
  return rows;
}

/** Os casos que o alerta carrega em `details.cases`, se carregar algum. */
function listedCases(
  alert: SalesOperationalAlert,
): PendingInvoiceBeyondLimitCase[] {
  const cases = alert.details.cases;
  return Array.isArray(cases) ? (cases as PendingInvoiceBeyondLimitCase[]) : [];
}

/**
 * `periodEndsAt` é dia (`YYYY-MM-DD`), não instante — formatado em UTC de
 * propósito: lido no fuso local, um `2026-06-03` vira 02/06 à noite.
 *
 * Fatura sem período existe (fatura futura criada em bloco, ou fatura da
 * Hotmart, cujo histórico não diz período) e não vira data inventada.
 */
function formatDueDay(value: string | undefined): string {
  if (!value) return 'sem período registrado';
  const parsed = Date.parse(`${value}T12:00:00Z`);
  if (Number.isNaN(parsed)) return value;
  return `venceu em ${new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(parsed))}`;
}

/**
 * A lista de faturas paradas, como linhas do painel expansível.
 *
 * **Todos os casos que vierem**, sem recorte próprio: o back já limita em 200
 * e marca `truncated`, e esconder parte do que ele mandou faria a tela mentir
 * duas vezes sobre o mesmo conjunto. São 200 linhas num painel que só abre
 * quando alguém clica.
 */
function invoiceCaseRows(
  alert: SalesOperationalAlert,
): PendingAlertDetailRow[] | undefined {
  const cases = listedCases(alert);
  if (cases.length === 0) return undefined;
  return cases.map((one) => ({
    label: `Ciclo ${one.cycleIndex ?? '?'} · ${formatDueDay(one.periodEndsAt)}`,
    value: one.invoiceId ?? '(sem id)',
    href:
      one.billingAgreementId && one.invoiceId
        ? `/billing-agreements/${one.billingAgreementId}?${FOCUSED_INVOICE_PARAM}=${one.invoiceId}`
        : undefined,
  }));
}

function invoiceCasesNote(alert: SalesOperationalAlert): string | undefined {
  const cases = listedCases(alert);
  if (cases.length === 0) return undefined;
  const limitDays = alert.details.limitDays;
  const since =
    typeof limitDays === 'number' ? ` há mais de ${limitDays} dias` : '';
  const truncated =
    alert.details.truncated === true
      ? ' Lista truncada — o conjunto completo sai de uma consulta às faturas.'
      : '';
  return `${cases.length} fatura(s) ainda pendente(s)${since}.${truncated}`;
}

/**
 * O que o back guardou sobre a recusa que abriu o alerta de conta de gateway.
 *
 * Esta linha é a **única** que o operador verá sobre o incidente inteiro — o
 * dedupe por tipo engole toda recusa seguinte enquanto o alerta estiver aberto
 * —, então o `details` precisa aparecer: sem ele a tela diz "o cadastro de
 * alguma conta está errado" e nada mais.
 *
 * O `identifierFingerprint` são 12 hex do SHA-256 do identificador recusado, e
 * é o que o back gravou **no lugar** do valor: o identificador é o Account
 * Token do gateway, um segredo (`.claude/rules/secrets.md` no monolith). Não
 * volta ao valor e serve só para comparar uma recusa com outra.
 */
function providerAccountRows(
  alert: SalesOperationalAlert,
): PendingAlertDetailRow[] | undefined {
  const rows: PendingAlertDetailRow[] = [];
  const push = (label: string, value: unknown) => {
    if (typeof value === 'string' && value) rows.push({ label, value });
  };
  push('Gateway', alert.details.provider);
  push('Evento recusado', alert.details.eventType);
  push('Impressão do identificador', alert.details.identifierFingerprint);
  return rows.length > 0 ? rows : undefined;
}

/**
 * A lista que a linha abre, por tipo de alerta. Tipo sem nada a mostrar (ou
 * tipo novo que esta tela ainda não conhece) não renderiza painel — a linha
 * fica como qualquer outra fonte.
 */
function detailsOf(alert: SalesOperationalAlert): {
  detailRows?: PendingAlertDetailRow[];
  detailsSummary?: string;
  detailsNote?: string;
} {
  if (alert.type === 'duplicate_payment') {
    const rows = duplicatePaymentRows(alert);
    if (rows.length === 0) return {};
    return {
      detailRows: rows,
      detailsSummary: 'Ver transações',
      detailsNote:
        'Um alerta por fatura: um novo pagamento duplicado nela entra nesta lista em vez de abrir outra linha.',
    };
  }
  if (alert.type === 'provider_account_not_recognized') {
    const rows = providerAccountRows(alert);
    if (!rows) return {};
    return {
      detailRows: rows,
      detailsSummary: 'Ver detalhes da recusa',
      // Honesto sobre o que a impressão é: a da PRIMEIRA recusa. O alerta não
      // é reescrito depois de aberto, então ela não acompanha o que está
      // chegando agora — quem compara é quem investiga.
      detailsNote:
        'Enquanto este alerta estiver aberto, nenhuma outra recusa abre linha — nem de outro gateway. A impressão é a da primeira recusa; compará-la com o que o gateway manda agora diz se é sempre a mesma conta.',
    };
  }
  const rows = invoiceCaseRows(alert);
  if (!rows) return {};
  return { detailRows: rows, detailsNote: invoiceCasesNote(alert) };
}

/**
 * Fonte de pendências do Sales: os **alertas operacionais** do módulo, a única
 * fila dele — o pagamento duplicado deixou de ter fila própria e virou o tipo
 * `duplicate_payment`.
 *
 * O grão do dedupe é por tipo no back (`.claude/rules/alerts.md`): a maioria
 * tem um aberto por tipo, o pagamento duplicado tem um aberto **por fatura**.
 *
 * **Fechar aqui é o que destrava o dedupe.** Enquanto houver um alerta `OPEN`
 * cobrindo a ocorrência, ela não grava row nem manda e-mail (`SalesAlerts`) —
 * sem esta fonte, o primeiro alerta ficava aberto para sempre e calava os
 * seguintes.
 *
 * `status: 'OPEN'` é maiúsculo: é o `OperationalAlertStatus` do shared.
 */
export const salesAlertsPendingSource: PendingSource = {
  key: 'sales-alerts',
  label: 'Vendas',
  requiredRoles: SALES_ROLES,
  async fetch({ page, perPage, type }) {
    const repository = container.resolve(SalesRepositoryToken);
    const response = await repository.listOperationalAlerts({
      page,
      perPage,
      status: 'OPEN',
      type: type || undefined,
      order: 'asc',
    });
    return {
      items: response.items.map((alert) => ({
        id: alert.id,
        sourceLabel: 'Vendas',
        // O feed agregado é minúsculo; o alerta responde maiúsculo (shared).
        status: alert.status.toLowerCase() as PendingAlertStatus,
        statusLabel: STATUS_LABEL[alert.status] ?? alert.status,
        typeLabel:
          ALERT_TYPE_LABEL[alert.type] ?? humanizeSnakeCase(alert.type),
        entityTypeLabel:
          ALERT_ENTITY_TYPE_LABEL[alert.entityType] ??
          humanizeSnakeCase(alert.entityType),
        entityId: alert.entityId,
        entityHref: alertEntityHref(alert),
        ...detailsOf(alert),
        closableForEntity: CLOSABLE_FOR_ENTITY.has(alert.type) || undefined,
        inThisStatusSince: alert.inThisStatusSince,
      })),
      total: response.total,
    };
  },
  async fetchFacets() {
    const repository = container.resolve(SalesRepositoryToken);
    const facets = await repository.listOperationalAlertFacets('OPEN');
    return facets.map(({ value, count }) => ({
      value,
      label: ALERT_TYPE_LABEL[value] ?? humanizeSnakeCase(value),
      count,
    }));
  },
  actions: {
    // Mesma lista da leitura: o eixo viewer × editor de Vendas foi removido em
    // `20260904120000-remove-unused-admin-roles`.
    writeRoles: SALES_ROLES,
    async close(id, resolved, input) {
      const repository = container.resolve(SalesRepositoryToken);
      if (resolved) {
        await repository.resolveOperationalAlert(id, input);
      } else {
        await repository.dismissOperationalAlert(id, input);
      }
    },
    async uploadAttachment(file) {
      const repository = container.resolve(SalesRepositoryToken);
      return repository.uploadAttachment(file);
    },
    async fetchHistory(id, { page, perPage }) {
      const repository = container.resolve(SalesRepositoryToken);
      const response = await repository.getEntityEvents(
        SALES_OPERATIONAL_ALERT_ENTITY_TYPE,
        id,
        { page, perPage },
      );
      return {
        items: response.items.map((event) => ({
          id: event.id,
          eventLabel: eventLabel(event.eventType),
          isSystem: event.actorPlatformProfileId === null,
          actorName: event.actorName ?? null,
          reason: event.reason,
          createdAt: event.createdAt,
          payload: event.payload,
        })),
        total: response.total,
      };
    },
  },
};
